// src/pages/SelectedProfiles.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/SelectedProfiles.css";
import { getJson } from "../network/api";
import { ApiEndpoints } from "../network/config";
import { useLocation } from "react-router-dom";

const SelectedProfiles = () => {
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("userId");
  const [profiles, setProfiles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    limit: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const location = useLocation();
  const [pageInput, setPageInput] = useState("");

  // 🔁 Load selected profiles for a given page
  const loadProfiles = async (page = 1) => {
    if (!userId) {
      setError("Please login to view selected profiles.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await getJson(ApiEndpoints.selectedProfiles, {
        params: {
          page,
          limit: 20,
          appUserId: userId,
        },
      });

      // Expecting: { data: [...], pagination_data: {...} }
      setProfiles(res.data || []);
      setPagination(res.pagination_data || pagination);
    } catch (err) {
      console.error("SelectedProfiles API Error:", err);
      setError("Failed to load selected profiles.");
    } finally {
      setLoading(false);
    }
  };

  // Load first page on mount
  useEffect(() => {
    const savedPage =
      location.state?.page ||
      sessionStorage.getItem("selectedProfilesPage") ||
      1;

    loadProfiles(Number(savedPage));
  }, []);

  // Pagination handlers
  const handleFirst = () => loadProfiles(1);

  const handlePrev = () => {
    if (pagination.page > 1) {
      loadProfiles(pagination.page - 1);
    }
  };

  const handleNext = () => {
    if (pagination.page < pagination.pages) {
      loadProfiles(pagination.page + 1);
    }
  };

  const handleLast = () => loadProfiles(pagination.pages);

  const handlePageJump = () => {
    const pageNum = Number(pageInput);

    if (!pageNum || pageNum < 1 || pageNum > pagination.pages) return;

    loadProfiles(pageNum);
  };

  const getPageNumbers = () => {
    const totalPages = pagination.pages;
    const current = pagination.page;
    const delta = 2;

    const pages = [];

    const rangeStart = Math.max(2, current - delta);
    const rangeEnd = Math.min(totalPages - 1, current + delta);

    pages.push(1);

    if (rangeStart > 2) pages.push("...");

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (rangeEnd < totalPages - 1) pages.push("...");

    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  // UI: loading / error
  if (loading) {
    return (
      <p style={{ textAlign: "center", marginTop: "90px" }}>Loading...</p>
    );
  }

  if (error) {
    return (
      <p style={{ textAlign: "center", marginTop: "30px", color: "red" }}>
        {error}
      </p>
    );
  }

  // Dynamic count of selected profiles
  const totalSelected = pagination.total || profiles.length || 0;

  return (
    <div className="selectedprofiles-container">
      {/* Header */}
      <div className="header-section">
        <div className="header-left">
          <h1 className="main-title">Selected Profiles</h1>
        </div>
      </div>

      {/* Top bar */}
      <div className="top-bar">
        <div className="profiles-selected-text">
          Profiles Selected : {totalSelected}
        </div>
      </div>

      <div className="profile-select-note">
        Once a profile is selected, it cannot be deselected.*
      </div>

      {/* Profiles grid */}
      <div className="profiles-grid">
        {profiles.length === 0 && (
          <p style={{ textAlign: "center", width: "100%" }}>
            No selected profiles found.
          </p>
        )}

        {profiles.map((p) => {
          // 🔍 Safely pick fields from API
          const key =
            p.id ||
            p.profileId ||
            p.fileNo ||
            `${p.sgsNo || "profile"}-${Math.random()}`;

          // Name: try multiple possible field names
          const name =
            p.name ||
            p.fullName ||
            p.candidateName ||
            p.profileName ||
            "Name Not Available";


          const phone =
            p.phone ||
            p.phoneNo ||
            p.contactNo ||
            p.mobileNo ||
            p.contactNumber ||
            p.mobileNumber ||
            p.mobile ||
            "";


          // Image: handle nested profileImages or direct url
          let imageUrl = "";
          if (Array.isArray(p.profileImages) && p.profileImages.length > 0) {
            const firstImage = p.profileImages[0];
            imageUrl =
              (firstImage && (firstImage.imageUrl || firstImage.url)) || "";
          } else {
            imageUrl =
              p.imageUrl ||
              p.profileImageUrl ||
              ""; // fallback: blank, CSS will handle
          }

          return (
            <div key={key} className="profile-card">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={name}
                  className="profile-image"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    console.error("Image failed:", imageUrl);
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div className="no-image-box">No Image</div>
              )}

              <div className="profile-info">
                {/* If API has SGS / File No you can show them too */}
                {p.sgsNo && <p>{p.sgsNo}</p>}
                {p.fileNo && <p>{p.fileNo}</p>}

                <p>{name}</p>
                {phone ? (
                  <a
                    href={`tel:${phone}`}
                    className="phone-link"
                    style={{
                      color: "blue",
                      textDecoration: "underline",
                      cursor: "pointer",
                      marginBottom: "5px",
                      display: "block"
                    }}
                  >
                    {phone}
                  </a>
                ) : (
                  <p>No Phone</p>
                )}

                <button
                  className="more-info"
                  onClick={() => {
                    sessionStorage.setItem("selectedProfilesPage", pagination.page);

                    navigate("/selected-profile-info", {
                      state: {
                        profileId: p.id || p.profileId,
                        page: pagination.page,
                      },
                    });
                  }}
                >
                  More Info
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination bar */}
      {!loading && !error && profiles.length > 0 && (
        <div className="pagination-bar">

          {/* ROW 1 */}
          <div className="pagination-row">

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(1)}
              disabled={pagination.page === 1}
            >
              &#171;
            </button>

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              &#8592;
            </button>

            {getPageNumbers().map((p, index) =>
              p === "..." ? (
                <span key={index} className="pagination-dots">...</span>
              ) : (
                <button
                  key={p}
                  className={`pagination-btn ${p === pagination.page ? "active" : ""}`}
                  onClick={() => loadProfiles(p)}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
            >
              &#8594;
            </button>

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.pages)}
              disabled={pagination.page === pagination.pages}
            >
              &#187;
            </button>

          </div>

          {/* ROW 2 */}
          <div className="pagination-row jump-row">
            <span>Go to page:</span>

            <input
              type="number"
              min="1"
              max={pagination.pages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              className="page-input"
            />

            <button className="pagination-btn" onClick={handlePageJump}>
              Go
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default SelectedProfiles;
